"use client";

import {
    editMemoryItem,
    removeMemoryItem,
    togglePinMemoryItem,
} from "@/app/(intelligence)/intelligence/(logged-in)/chat/memory/actions";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { MemoryItem } from "@/db/schema/memory";
import { useEffect, useState } from "react";

interface MemoryBankProps {
    memories: MemoryItem[];
    patientId: string;
}

export function MemoryBank({
    memories: initialMemories,
    patientId,
}: MemoryBankProps) {
    const [memories, setMemories] = useState<MemoryItem[]>(initialMemories);
    const [activeCategory, setActiveCategory] = useState("all");
    const [filteredMemories, setFilteredMemories] =
        useState<MemoryItem[]>(initialMemories);
    const [editingMemory, setEditingMemory] = useState<MemoryItem | null>(null);
    const [isEditing, setIsEditing] = useState(false);

    // Filter memories when category changes
    useEffect(() => {
        if (activeCategory === "all") {
            setFilteredMemories(memories);
        } else {
            setFilteredMemories(
                memories.filter((m) => m.category === activeCategory),
            );
        }
    }, [activeCategory, memories]);

    const handleEdit = (memory: MemoryItem) => {
        setEditingMemory(memory);
        setIsEditing(true);
    };

    const handleSave = async () => {
        if (!editingMemory) return;

        try {
            const updatedMemory = await editMemoryItem({
                id: editingMemory.id,
                title: editingMemory.title,
                content: editingMemory.content,
                category: editingMemory.category as any,
                isPinned: editingMemory.is_pinned,
            });

            // Update local state
            setMemories(
                memories.map((m) =>
                    m.id === editingMemory.id ? { ...m, ...editingMemory } : m,
                ),
            );

            // Close dialog
            setIsEditing(false);
            setEditingMemory(null);
        } catch (error) {
            console.error("Failed to update memory", error);
        }
    };

    const handleDelete = async (id: string) => {
        try {
            await removeMemoryItem({ id });

            // Update local state
            setMemories(memories.filter((m) => m.id !== id));
        } catch (error) {
            console.error("Failed to delete memory", error);
        }
    };

    const handlePin = async (id: string, isPinned: boolean) => {
        try {
            await togglePinMemoryItem({ id, isPinned });

            // Update local state
            setMemories(
                memories.map((m) =>
                    m.id === id ? { ...m, is_pinned: !isPinned } : m,
                ),
            );
        } catch (error) {
            console.error("Failed to update pin status", error);
        }
    };

    return (
        <div className="memory-bank">
            <Tabs defaultValue="all" onValueChange={setActiveCategory}>
                <TabsList className="mb-4">
                    <TabsTrigger value="all">All</TabsTrigger>
                    <TabsTrigger value="health">Health</TabsTrigger>
                    <TabsTrigger value="preference">Preferences</TabsTrigger>
                    <TabsTrigger value="fact">Facts</TabsTrigger>
                    <TabsTrigger value="goal">Goals</TabsTrigger>
                    <TabsTrigger value="other">Other</TabsTrigger>
                </TabsList>

                <TabsContent value={activeCategory} className="space-y-4">
                    {filteredMemories.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            No memories found in this category.
                        </div>
                    ) : (
                        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                            {filteredMemories.map((memory) => (
                                <Card
                                    key={memory.id}
                                    className={`flex flex-col h-full ${
                                        memory.is_pinned ? "border-primary" : ""
                                    }`}
                                >
                                    <CardHeader className="pb-2">
                                        <div className="flex justify-between items-start">
                                            <CardTitle className="text-lg">
                                                {memory.title}
                                            </CardTitle>
                                            <span className="text-xs text-muted-foreground">
                                                {new Date(
                                                    memory.created_at,
                                                ).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <div className="text-xs font-medium text-muted-foreground uppercase">
                                            {memory.category}
                                        </div>
                                    </CardHeader>
                                    <CardContent className="pb-2 flex-grow">
                                        <p className="text-sm">
                                            {memory.content}
                                        </p>
                                    </CardContent>
                                    <CardFooter className="pt-2 flex justify-between">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() =>
                                                handlePin(
                                                    memory.id,
                                                    memory.is_pinned,
                                                )
                                            }
                                        >
                                            {memory.is_pinned ? "Unpin" : "Pin"}
                                        </Button>
                                        <div className="space-x-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() =>
                                                    handleEdit(memory)
                                                }
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={() =>
                                                    handleDelete(memory.id)
                                                }
                                            >
                                                Delete
                                            </Button>
                                        </div>
                                    </CardFooter>
                                </Card>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            {/* Edit Dialog */}
            {editingMemory && (
                <Dialog open={isEditing} onOpenChange={setIsEditing}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Edit Memory</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">
                                    Title
                                </label>
                                <Input
                                    value={editingMemory.title}
                                    onChange={(e) =>
                                        setEditingMemory({
                                            ...editingMemory,
                                            title: e.target.value,
                                        })
                                    }
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">
                                    Content
                                </label>
                                <Textarea
                                    value={editingMemory.content}
                                    onChange={(e) =>
                                        setEditingMemory({
                                            ...editingMemory,
                                            content: e.target.value,
                                        })
                                    }
                                    rows={4}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">
                                    Category
                                </label>
                                <Select
                                    value={editingMemory.category}
                                    onValueChange={(value) =>
                                        setEditingMemory({
                                            ...editingMemory,
                                            category: value as any,
                                        })
                                    }
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="health">
                                            Health
                                        </SelectItem>
                                        <SelectItem value="preference">
                                            Preference
                                        </SelectItem>
                                        <SelectItem value="fact">
                                            Fact
                                        </SelectItem>
                                        <SelectItem value="goal">
                                            Goal
                                        </SelectItem>
                                        <SelectItem value="other">
                                            Other
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="pin-memory"
                                    checked={editingMemory.is_pinned}
                                    onChange={(e) =>
                                        setEditingMemory({
                                            ...editingMemory,
                                            is_pinned: e.target.checked,
                                        })
                                    }
                                />
                                <label
                                    htmlFor="pin-memory"
                                    className="text-sm font-medium"
                                >
                                    Pin this memory
                                </label>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => setIsEditing(false)}
                            >
                                Cancel
                            </Button>
                            <Button onClick={handleSave}>Save Changes</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}
